import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Updated to match GatewayHomePage background theme
const headerBackground = `
  radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.08) 0%, transparent 50%),
  radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.08) 0%, transparent 50%),
  linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%)
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;



const HeaderContainer = styled.header`
  background: ${headerBackground};
  color: #fff;
  padding: 1.2rem 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  position: sticky;
  top: 0;
  z-index: 1000;
  animation: ${fadeIn} 0.5s ease-out;
  border-bottom: 1px solid rgba(110, 168, 255, 0.15);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1400px;
  position: relative;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 1rem;
`;

const BackToGatewayLink = styled(Link)`
  color: #b6d0ff;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(110, 168, 255, 0.1);
  
  &:hover {
    background: rgba(110, 168, 255, 0.2);
    color: #fff;
    transform: translateX(-2px);
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.6rem 1rem;
  }
`;

const Center = styled.div`
  display: flex;
  flex: 1 1 0%;
  justify-content: center;
  align-items: center;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`;

const Nav = styled.nav`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    display: ${props => (props.$isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${headerBackground};
    padding: 5rem 1rem 1rem;
    gap: 1.5rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    z-index: 100;
    animation: ${slideIn} 0.3s ease-out;
    overflow-y: auto;
  }
`;

const NavItem = styled(Link)`
  color: #b6d0ff;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: relative;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  
  &:hover {
    background: rgba(110, 168, 255, 0.15);
    color: #fff;
    transform: translateY(-1px);
  }
  
  ${props => props.$active && `
    color: #fff;
    background: rgba(110, 168, 255, 0.25);
    
    &:hover {
      background: rgba(110, 168, 255, 0.35);
      transform: translateY(-1px);
    }
  `}
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    opacity: ${props => props.$isOpen ? '1' : '0'};
    transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(20px)'};
    transition: all 0.3s ease;
    transition-delay: ${props => props.$index * 0.1}s;
  }
`;

const SupportButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;

  @media (max-width: 768px) {
    position: static;
    margin-top: 0;
    width: auto;
    justify-content: flex-end;
  }
`;

const SupportButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  background: rgba(110, 168, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(110, 168, 255, 0.3);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem 1.5rem;
    font-size: 1rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(110, 168, 255, 0.1);
  border: none;
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.75rem;
  z-index: 1000;
  transition: all 0.3s ease;
  border-radius: 8px;
  
  &:hover {
    background: rgba(110, 168, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    display: block;
    position: static;
    margin-right: 0.5rem;
  }
`;

const Overlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 90;
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('nav') && !event.target.closest('button')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  const handleNavClick = () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <HeaderContainer style={{ 
      boxShadow: isScrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: isScrolled ? (window.innerWidth <= 768 ? '0.8rem 1rem' : '1rem 2rem') : (window.innerWidth <= 768 ? '1rem 1rem' : '1.2rem 2rem'),
    }}>
      <HeaderContent>
        <Left>
          {location.pathname !== '/' && location.pathname !== '' && (
            <BackToGatewayLink to="/">
              ← Gateway
            </BackToGatewayLink>
          )}
          <MobileMenuButton onClick={toggleMobileMenu} aria-label="Toggle menu">
            {isMobileMenuOpen ? '✕' : '☰'}
          </MobileMenuButton>
        </Left>
        <Center>
          <Nav $isOpen={isMobileMenuOpen}>
            <NavItem to="/warmer" $active={location.pathname === '/warmer'} onClick={handleNavClick} $isOpen={isMobileMenuOpen} $index={0}>
              Warmer
            </NavItem>
            <NavItem to="/buy" $active={location.pathname === '/buy'} onClick={handleNavClick} $isOpen={isMobileMenuOpen} $index={1}>
              Pricing
            </NavItem>
            <NavItem to="/docs" $active={location.pathname === '/docs'} onClick={handleNavClick} $isOpen={isMobileMenuOpen} $index={2}>
              Docs
            </NavItem>
            <NavItem to="/changelog" $active={location.pathname === '/changelog'} onClick={handleNavClick} $isOpen={isMobileMenuOpen} $index={3}>
              Changelog
            </NavItem>
          </Nav>
        </Center>
        <Right>
          <SupportButtons>
            <SupportButton as={Link} to="/docs/contact-support" onClick={handleNavClick}>
              Contact
            </SupportButton>
          </SupportButtons>
        </Right>
      </HeaderContent>
      <Overlay $isOpen={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(false)} />
    </HeaderContainer>
  );
}

export default Header; 